import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { isEmpty } from 'lodash'
import {
  EduButton,
  EduIf,
  FlexContainer,
  SelectInputStyled,
  TextAreaInputStyled,
} from '@edulastic/common'
import { Spin } from 'antd'

import {
  LANGUAGES_OPTIONS,
  LANGUAGE_EN,
} from '@edulastic/constants/const/languages'
import connect from 'react-redux/es/connect/connect'

import { IconGoogleTranslate } from '@edulastic/icons'
import { white } from '@edulastic/colors'
import { TTS_ENABLED_PASSAGE_TYPES } from '@edulastic/constants/const/questionType'
import { StyledSpeakableTextContainer } from './styled-components'
import AudioControls from '../../../../../../assessment/AudioControls'
import { allowedToSelectMultiLanguageInTest } from '../../../../selectors/user'
import {
  changeDataToPreferredLanguage,
  isEnglishContentEmpty,
} from '../../../../../../assessment/utils/question'
import VoiceLanguageSelectorComponent from './VoiceLanguageSelectorComponent'

const PassageSpeakableText = ({
  ttsTextAPIStatus,
  updateTTSAPIStatus,
  ttsTextData = {},
  updateTTSText,
  sourceData: passage,
  showTTSTextModal,
  regenerateTTSText,
  onLanguageChange,
  selectedLanguage,
  allowedToSelectMultiLanguage,
  onChangeVoiceLanguge,
  voiceLanguage,
}) => {
  const [updatedTtsData, setUpdatedTtsData] = useState(ttsTextData)

  useEffect(() => {
    setUpdatedTtsData(ttsTextData)
  }, [ttsTextData])

  const {
    heading = '',
    contentsTitle = '',
    source = '',
    content = '',
    pages = [],
  } = updatedTtsData || {}

  const handleUpdateTtsData = (text, type, index) => {
    if (type === 'pages' && typeof index === 'number') {
      setUpdatedTtsData((previousState) => {
        const pagesToModify = previousState?.pages || []
        if (
          index < 0 ||
          index >= pagesToModify.length ||
          pagesToModify[index] === text
        ) {
          return previousState
        }
        return {
          ...previousState,
          pages: pagesToModify.map((page, i) => (i === index ? text : page)),
        }
      })
      return
    }
    setUpdatedTtsData((previousState) => ({ ...previousState, [type]: text }))
  }

  const handleUpdateTTSText = () => {
    updateTTSText(updatedTtsData)
  }

  if (isEmpty(passage)) {
    return null
  }

  const passageDataByLanguage = changeDataToPreferredLanguage(
    passage,
    selectedLanguage
  )
  const audioSrc = passageDataByLanguage?.tts?.titleAudioURL

  const isEnglishLanguageDisable = isEnglishContentEmpty(passage)

  // Determine if passage is paginated - check if it has pages array or paginated_content flag
  // AudioControls uses questionData.paginated_content to determine if it should load from tts.pages
  const isPaginated = passageDataByLanguage?.paginated_content

  const showLanguageSelector =
    allowedToSelectMultiLanguage &&
    TTS_ENABLED_PASSAGE_TYPES.includes(passage.type) &&
    Object.keys(passage?.languageFeatures || {}).length

  return (
    <>
      <EduIf condition={showLanguageSelector}>
        <FlexContainer justifyContent="flex-end">
          <SelectInputStyled
            data-cy="tts-language-selector"
            width="120px"
            height="30px"
            onSelect={(value) => onLanguageChange(value)}
            value={selectedLanguage}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            bg={white}
          >
            {LANGUAGES_OPTIONS.filter((languageOption) => {
              const isLanguageInQuestion =
                languageOption.value === LANGUAGE_EN
                  ? !isEnglishLanguageDisable
                  : passage?.languageFeatures?.[languageOption.value]
              return !languageOption.isTTSDisabled && isLanguageInQuestion
            }).map((language) => (
              <option value={language.value} key={language.value}>
                <FlexContainer justifyContent="center" alignItems="center">
                  <IconGoogleTranslate margin="0 5px 0 0" />
                  {language.label}
                </FlexContainer>
              </option>
            ))}
          </SelectInputStyled>
        </FlexContainer>
      </EduIf>
      <EduIf condition={ttsTextAPIStatus === 'INITIATED'}>
        <Spin style={{ marginTop: '20px' }} />
      </EduIf>
      <EduIf condition={ttsTextAPIStatus === 'SUCCESS'}>
        <StyledSpeakableTextContainer
          margin={showLanguageSelector ? '0px' : '10px 0 0 0'}
        >
          <EduIf condition={(audioSrc || '').length > 0 && showTTSTextModal}>
            <AudioControls
              key={passage?.id}
              item={passageDataByLanguage}
              qId={passage?.id}
              audioSrc={audioSrc}
              className="speakable-text-audio-controls"
              isMinimalDesign
              page={1}
              isPaginated={isPaginated}
            />
          </EduIf>
          <StyledTTStextWrapper>
            <h4>Heading</h4>
            <TextAreaInputStyled
              style={{ paddingLeft: '5px', paddingTop: '5px' }}
              value={heading || ''}
              onChange={(e) =>
                handleUpdateTtsData(e?.target?.value || '', 'heading')
              }
            />
          </StyledTTStextWrapper>
          <StyledTTStextWrapper>
            <h4>Title</h4>
            <TextAreaInputStyled
              style={{ paddingLeft: '5px', paddingTop: '5px' }}
              value={contentsTitle || ''}
              onChange={(e) =>
                handleUpdateTtsData(e?.target?.value || '', 'contentsTitle')
              }
            />
          </StyledTTStextWrapper>
          <StyledTTStextWrapper>
            <h4>Author/Source</h4>

            <TextAreaInputStyled
              style={{ paddingLeft: '5px', paddingTop: '5px' }}
              value={source || ''}
              onChange={(e) =>
                handleUpdateTtsData(e?.target?.value || '', 'source')
              }
            />
          </StyledTTStextWrapper>
          <StyledTTStextWrapper>
            {pages?.length > 0 &&
              (pages || [])?.map((page, index) => (
                <>
                  <h4>Content page {index + 1}</h4>
                  <TextAreaInputStyled
                    style={{
                      paddingLeft: '5px',
                      paddingTop: '5px',
                      marginBottom: '10px',
                    }}
                    value={page || ''}
                    onChange={(e) =>
                      handleUpdateTtsData(
                        e?.target?.value || '',
                        'pages',
                        index
                      )
                    }
                  />
                </>
              ))}
            {content && (
              <>
                <h4>Content</h4>
                <TextAreaInputStyled
                  style={{
                    paddingLeft: '5px',
                    paddingTop: '5px',
                    marginBottom: '10px',
                  }}
                  value={content}
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  onChange={(e) =>
                    handleUpdateTtsData(e?.target?.value || '', 'content')
                  }
                />
              </>
            )}
          </StyledTTStextWrapper>

          <FlexContainer justifyContent="space-between">
            <FlexContainer justifyContent="flex-start">
              <EduIf condition={!showLanguageSelector}>
                <VoiceLanguageSelectorComponent
                  onChangeVoiceLanguge={onChangeVoiceLanguge}
                  voiceLanguage={voiceLanguage}
                />
              </EduIf>
            </FlexContainer>
            <FlexContainer justifyContent="flex-end" alignItems="center">
              <EduButton
                isGhost
                loading={ttsTextAPIStatus === 'INITIATED'}
                onClick={regenerateTTSText}
                disabled={ttsTextAPIStatus === 'INITIATED'}
              >
                Regenerate TTS Text
              </EduButton>
              <EduButton
                loading={updateTTSAPIStatus === 'INITIATED'}
                onClick={handleUpdateTTSText}
                disabled={updateTTSAPIStatus === 'INITIATED'}
              >
                Generate TTS Audio
              </EduButton>
            </FlexContainer>
          </FlexContainer>
        </StyledSpeakableTextContainer>
      </EduIf>
    </>
  )
}

export default connect((state) => ({
  allowedToSelectMultiLanguage: allowedToSelectMultiLanguageInTest(state),
}))(PassageSpeakableText)

const StyledTTStextWrapper = styled(FlexContainer)`
  overflow: auto;
  flex-direction: column;
  justify-content: flex-start;
`
